import React, { useState, useEffect, useContext } from 'react';
import axios from '../api/axios';
import { FiShoppingCart, FiZap } from 'react-icons/fi';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';

const GiftCardItem = ({ giftCard, onAddToCart, onQuickBuy }) => {
  const [selectedDenomination, setSelectedDenomination] = useState(
    giftCard.denominations[0]?.value || 0
  );
  const navigate = useNavigate();

  // Category name mapping
  const categoryMap = {
    'A': 'Accessories',
    'B': 'Baby & Kids',
    'C': 'Communication',
    'E': 'Electronics',
    'F': 'Fashion & Apparel',
    'G': 'General Merchandise',
    'H': 'Home & Living',
    'M': 'Mobile & Telecom',
    'R': 'Restaurant & Dining',
    'T': 'Travel & Entertainment',
    'W': 'Wellness & Sports'
  };

  const categoryName = categoryMap[giftCard.category] || giftCard.category;

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition">
      <div 
        onClick={() => navigate(`/catalog/${giftCard._id}`)}
        className="cursor-pointer"
      >
        <img
          src={giftCard.image}
          alt={giftCard.name}
          className="w-full h-48 object-cover"
        />
        <div className="p-4">
          <h3 className="text-xl font-semibold mb-2">{giftCard.name}</h3>
          <p className="text-gray-600 text-sm mb-2">{categoryName}</p>
          <p className="text-gray-500 text-sm mb-4 line-clamp-2">{giftCard.description}</p>
        </div>
      </div>
      
      <div className="px-4 pb-4">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Amount
          </label>
          <select
            value={selectedDenomination}
            onChange={(e) => setSelectedDenomination(Number(e.target.value))}
            onClick={(e) => e.stopPropagation()}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {giftCard.denominations.map((denom) => (
              <option key={denom.value} value={denom.value}>
                ${denom.value}
              </option>
            ))}
          </select>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddToCart(giftCard._id);
            }}
            className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition flex items-center justify-center space-x-2"
          >
            <FiShoppingCart />
            <span>Add to Cart</span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onQuickBuy(giftCard._id, selectedDenomination);
            }}
            className="flex-1 bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-600 transition flex items-center justify-center space-x-2"
          >
            <FiZap />
            <span>Quick Buy</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const Catalog = () => {
  const [giftCards, setGiftCards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [userCountry, setUserCountry] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [locationDetected, setLocationDetected] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { addToCart } = useContext(CartContext);
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const initializePage = async () => {
      await detectUserLocation();
      loadCategories();
      loadCountries();
      setLocationDetected(true);
    };
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (locationDetected) {
      setPage(1);
      setGiftCards([]);
      loadGiftCards(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedCountry, searchTerm, locationDetected]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      
      console.log(`Scroll - loadingMore: ${loadingMore}, hasMore: ${hasMore}, loading: ${loading}, distanceFromBottom: ${distanceFromBottom}`);
      
      if (loadingMore || !hasMore || loading) {
        console.log('Scroll blocked:', { loadingMore, hasMore, loading });
        return;
      }
      
      if (distanceFromBottom <= 500) {
        console.log('Scroll trigger: Loading next page', page + 1);
        setPage(prev => {
          console.log('Setting page from', prev, 'to', prev + 1);
          return prev + 1;
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, loadingMore, loading, page]);

  useEffect(() => {
    if (page > 1 && !loadingMore) {
      console.log(`Page changed to ${page}, loading...`);
      loadGiftCards(page, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const detectUserLocation = async () => {
    try {
      // Use our backend API to detect location (avoids CORS issues)
      const response = await axios.get('/api/location');
      const country = response.data.country;
      
      console.log('Location detection response:', response.data);
      console.log('Detected country:', country);
      
      if (!country) {
        console.warn('No country detected, using default');
        setUserCountry('USA');
        setSelectedCountry('USA');
        return;
      }
      
      console.log('Setting selectedCountry to:', country);
      
      setUserCountry(country);
      setSelectedCountry(country);
    } catch (err) {
      console.error('Failed to detect location:', err);
      // Default to USA on error (matches database format)
      console.log('Using fallback: USA');
      setUserCountry('USA');
      setSelectedCountry('USA');
    }
  };

  const loadGiftCards = async (pageNum, reset = false) => {
    console.log('=== loadGiftCards called ===');
    console.log('Page:', pageNum, 'Reset:', reset);
    console.log('State - selectedCountry:', selectedCountry);
    console.log('State - selectedCategory:', selectedCategory);
    console.log('State - searchTerm:', searchTerm);
    console.log('Already loading?', loadingMore || loading);
    
    // Prevent duplicate requests
    if (!reset && (loadingMore || loading)) {
      console.log('Blocked: already loading');
      return;
    }
    
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const params = {
        page: pageNum,
        limit: 50
      };
      if (selectedCategory) params.category = selectedCategory;
      if (selectedCountry) params.country = selectedCountry;
      if (searchTerm) params.search = searchTerm;

      console.log('Final API params object:', params);

      const res = await axios.get('/api/giftcards', { params });
      const { giftCards: newCards, pagination } = res.data;
      
      console.log(`API response - page ${pageNum}: ${newCards.length} cards, pagination:`, pagination);
      
      const updatedCards = reset ? newCards : [...giftCards, ...newCards];
      setGiftCards(updatedCards);
      setHasMore(pagination.hasMore);
      
      console.log(`Updated state - total cards: ${updatedCards.length}, hasMore: ${pagination.hasMore}`);
      
      // If we got very few results and there are more pages, auto-load next page after delay
      const minItemsNeeded = 20; // Minimum items to show before stopping auto-load
      
      if (newCards.length > 0 && pagination.hasMore && updatedCards.length < minItemsNeeded) {
        console.log(`Only ${updatedCards.length} items displayed, auto-loading next page in 2 seconds...`);
        
        // Wait 2 seconds to avoid rate limiting, then load next page
        setTimeout(() => {
          console.log('Auto-loading next page');
          setLoadingMore(false);
          setLoading(false);
          setPage(pageNum + 1);
        }, 2000);
        return; // Don't set loading states to false yet
      }
    } catch (err) {
      console.error('Error loading gift cards:', err);
      setHasMore(false);
    }
    setLoading(false);
    setLoadingMore(false);
    console.log('loadGiftCards completed');
  };

  const loadCategories = async () => {
    try {
      const res = await axios.get('/api/giftcards/meta/categories');
      setCategories(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadCountries = async () => {
    try {
      const res = await axios.get('/api/giftcards/meta/countries');
      setCountries(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddToCart = async (giftCardId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const result = await addToCart(giftCardId);
    if (result.success) {
      alert('Added to cart!');
    } else {
      alert(result.error);
    }
  };

  const handleQuickBuy = (giftCardId, denomination) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    navigate('/checkout', { state: { giftCardId, denomination, quickBuy: true } });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-4xl font-bold mb-8">Gift Card Catalog</h1>

      {/* Filters */}
      <div className="mb-8 flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search gift cards..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Countries</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country} {country === userCountry ? '(Your Location)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category.code} value={category.code}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Gift Cards Grid */}
      {loading ? (
        <LoadingSpinner message="Finding the best gift cards for you..." />
      ) : giftCards.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {giftCards.map((giftCard) => (
            <GiftCardItem
              key={giftCard._id}
              giftCard={giftCard}
              onAddToCart={handleAddToCart}
              onQuickBuy={handleQuickBuy}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <p className="text-xl">No gift cards found</p>
          <p className="mt-2">Try changing your filters or search term</p>
        </div>
      )}

      {/* Loading more indicator */}
      {loadingMore && (
        <div className="text-center py-8">
          <div className="inline-flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
            <span className="text-gray-600 font-medium">Loading more gift cards...</span>
          </div>
        </div>
      )}

      {/* End of results */}
      {!loading && !hasMore && giftCards.length > 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>No more gift cards to load</p>
        </div>
      )}
    </div>
  );
};

export default Catalog;
